import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';

const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimer = useRef(null);

  useEffect(() => {
    // Try to restore session from cached user profile (cookie is sent automatically)
    const stored = localStorage.getItem('ax_user');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // If cached user is missing allowedFeatures or allowedApps, re-fetch from server
        if (!parsed.allowedFeatures || !parsed.allowedApps) {
          api.post('/api/auth/refresh', {})
            .then(({ data }) => {
              localStorage.setItem('ax_user', JSON.stringify(data.user));
              setUser(data.user);
            })
            .catch(() => {
              localStorage.removeItem('ax_user');
            })
            .finally(() => setLoading(false));
          return;
        }
        setUser(parsed);
      } catch {
        localStorage.removeItem('ax_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post('/api/auth/login', { email, password });
    // Token is stored in httpOnly cookie by the server — never touches JS
    localStorage.setItem('ax_user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  const logout = useCallback(async () => {
    clearTimeout(inactivityTimer.current);
    await api.post('/api/auth/logout', {}).catch(() => {});
    localStorage.removeItem('ax_user');
    setUser(null);
  }, []);

  // Reset inactivity timer on user activity
  const resetTimer = useCallback(() => {
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => logout(), INACTIVITY_MS);
  }, [logout]);

  useEffect(() => {
    if (!user) return;
    const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer(); // start timer on login/mount
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(inactivityTimer.current);
    };
  }, [user, resetTimer]);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
